import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Save, Video, Trash2, Upload, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeaserVideosTab = () => {
  const [teasers, setTeasers] = useState(Array(6).fill(null).map(() => ({ url: '', title: '' })));
  const [liveTeasers, setLiveTeasers] = useState(Array(6).fill(null).map(() => ({ url: '', title: '' })));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingLive, setSavingLive] = useState(false);
  const [uploading, setUploading] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [res1, res2] = await Promise.all([
          axios.get(`${API_URL}/api/teaser-videos`),
          axios.get(`${API_URL}/api/live-event-teasers`)
        ]);
        const t = res1.data.videos || [];
        const l = res2.data.videos || [];
        setTeasers(Array(6).fill(null).map((_, i) => t[i] || { url: '', title: '' }));
        setLiveTeasers(Array(6).fill(null).map((_, i) => l[i] || { url: '', title: '' }));
      } catch { /* defaults */ }
      setLoading(false);
    };
    fetch();
  }, []);

  const handleUpload = async (type, index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const key = `${type}-${index}`;
    setUploading(key);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/admin/agreements/upload-doc`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const setter = type === 'teaser' ? setTeasers : setLiveTeasers;
      setter(prev => prev.map((v, i) => i === index ? { ...v, url: res.data.file_url } : v));
      toast.success('Video uploaded!');
    } catch {
      toast.error('Upload failed');
    }
    setUploading(null);
  };

  const saveTeasers = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/teaser-videos`, { videos: teasers.filter(v => v.url.trim()) });
      toast.success('Teaser videos saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const saveLiveTeasers = async () => {
    setSavingLive(true);
    try {
      await axios.put(`${API_URL}/api/admin/live-event-teasers`, { videos: liveTeasers.filter(v => v.url.trim()) });
      toast.success('Live event teasers saved');
    } catch { toast.error('Failed to save'); }
    setSavingLive(false);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner" /></div>;

  const renderSlots = (videos, setVideos, type, count) => (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {videos.slice(0, count).map((video, i) => (
        <Card key={i}>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-primary">
                <Video className="w-4 h-4" />
                <Label className="font-semibold text-sm">{type === 'teaser' ? 'Teaser' : 'Live Event'} {i + 1}</Label>
              </div>
              {video.url && (
                <button onClick={() => setVideos(prev => prev.map((v, j) => j === i ? { url: '', title: '' } : v))} className="text-muted-foreground hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <Input value={video.url} onChange={e => setVideos(prev => prev.map((v, j) => j === i ? { ...v, url: e.target.value } : v))} placeholder="Video URL (MP4)" />
            <label className="cursor-pointer">
              <input type="file" accept="video/mp4,video/quicktime,.mp4,.mov" onChange={(e) => handleUpload(type, i, e)} className="hidden" />
              <Button type="button" variant="outline" size="sm" asChild disabled={uploading === `${type}-${i}`}>
                <span>{uploading === `${type}-${i}` ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-1" /> Upload MP4</>}</span>
              </Button>
            </label>
            <Input value={video.title} onChange={e => setVideos(prev => prev.map((v, j) => j === i ? { ...v, title: e.target.value } : v))} placeholder="Caption (optional)" />
            {video.url && (
              <video src={video.url} className={`w-full rounded-lg bg-black ${type === 'teaser' ? 'aspect-[9/16]' : 'aspect-video'} object-cover`} muted loop autoPlay playsInline />
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="space-y-10" data-testid="teaser-videos-tab">
      {/* Teaser Videos (9:16) */}
      <div>
        <h3 className="font-display font-bold text-lg uppercase mb-2">Teaser Videos (Vertical 9:16)</h3>
        <p className="text-sm text-muted-foreground mb-4">6 short vertical clips for the hero section. MP4 format, vertical orientation.</p>
        {renderSlots(teasers, setTeasers, 'teaser', 6)}
        <Button onClick={saveTeasers} disabled={saving} className="rounded-full mt-4" data-testid="save-teaser-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Teasers'}
        </Button>
      </div>

      {/* Live Event Teasers (16:9) */}
      <div>
        <h3 className="font-display font-bold text-lg uppercase mb-2">Live Event Highlights (Landscape 16:9)</h3>
        <p className="text-sm text-muted-foreground mb-4">6 landscape clips shown below the teasers. MP4 format, 16:9 orientation.</p>
        {renderSlots(liveTeasers, setLiveTeasers, 'live', 6)}
        <Button onClick={saveLiveTeasers} disabled={savingLive} className="rounded-full mt-4" data-testid="save-live-teaser-btn">
          <Save className="w-4 h-4 mr-2" />{savingLive ? 'Saving...' : 'Save Live Event Teasers'}
        </Button>
      </div>
    </div>
  );
};

export default TeaserVideosTab;
